/**
 * Standardized units across this file:
 *   - All monetary amounts: integer cents (EUR), branded as `IntegerAmount<2>` via `makeIntegerAmount`.
 *     Use `X_NN` literal format for readability (`5_00` = €5.00).
 *   - All rates: decimal in [0, 1], branded as `DecimalAmount<N>` via `makeDecimalAmount` where `N`
 *     matches each value's natural precision. Multiply by 100 for percent (`0.005` = 0.5%).
 *
 * No mixed units. Every field is either `*_cents` (integer amount) or `*_rate` (decimal amount).
 */
import { makeDecimalAmount, makeIntegerAmount } from '@Common/types/currency/currency';

/** Decimal-place scales used to brand the `*_rate` values below. */
const SERVICE_FEE_RATE_SCALE = 3;
const ANNUAL_RATE_SCALE = 2;
const WM_COMPANY_RATE_SCALE = 5;

const wmCustodyMaxRate = {
  gia: makeDecimalAmount(0.01, WM_COMPANY_RATE_SCALE),
  pea: makeDecimalAmount(0.004, WM_COMPANY_RATE_SCALE),
  per: makeDecimalAmount(0.005, WM_COMPANY_RATE_SCALE),
} as const;

const wmTotalFeeMaxRate = {
  ...wmCustodyMaxRate,
  per: makeDecimalAmount(0.012, WM_COMPANY_RATE_SCALE),
} as const;

export const FEES = {
  investment_plan: {
    /** WM plans only. Tiered fee charged per execution; rate depends on which threshold the total amount falls between. */
    service_fee: {
      below_low_threshold_rate: makeDecimalAmount(0.005, SERVICE_FEE_RATE_SCALE),
      low_threshold_cents: makeIntegerAmount(200_00),
      above_high_threshold_rate: makeDecimalAmount(0.002, SERVICE_FEE_RATE_SCALE),
      high_threshold_cents: makeIntegerAmount(500_00),
      /** Flat floor applied between the two thresholds (and as minimum on GIA below high threshold). */
      flat_min_cents: makeIntegerAmount(1_00),
      /** Per-allocation cap when asset is a mutual fund, allocation-strategy plans only. */
      mutual_fund_alloc_cap_cents: makeIntegerAmount(50_00),
    },
    /** B2C managed plans only (is_managed_plan=true, not is_managed_by_wm). Charged on schedule, not per execution. */
    management_fee: { annual_rate: makeDecimalAmount(0.01, ANNUAL_RATE_SCALE) },
    /**
     * PER service fee (entry/exit), Libre management only. Pilotée (managed) is fee-free.
     * Free management applies a flat rate per operation with a €1 floor; mutual funds additionally cap at €50.
     * Other asset classes (FIA/ELTIF, structured products, stocks/ETFs EU+US, SCPI) share the rate and floor but have no cap.
     */
    per_service_fee: {
      managed_rate: makeDecimalAmount(0, SERVICE_FEE_RATE_SCALE),
      managed_min_cents: makeIntegerAmount(0),
      managed_max_cents: makeIntegerAmount(0),
      free_rate: makeDecimalAmount(0.002, SERVICE_FEE_RATE_SCALE),
      free_min_cents: makeIntegerAmount(1_00),
      free_mutual_fund_max_cents: makeIntegerAmount(50_00),
    },
    /** Plan-creation guards on the chosen total amount. */
    min_total_amount: {
      wm_gia_recurring_cents: makeIntegerAmount(50_00),
      wm_pea_recurring_cents: makeIntegerAmount(100_00),
      wm_gia_initial_cents: makeIntegerAmount(50_00),
      wm_pea_initial_cents: makeIntegerAmount(100_00),
      wm_initial_max_cents: makeIntegerAmount(20_000_000_00),
    },
    /** Display-only values that are NOT applied at runtime. */
    legacy_display: {
      flat_cents: makeIntegerAmount(0),
      unused_flat_cents: makeIntegerAmount(5_99),
      unused_unitary_cents: makeIntegerAmount(2_00),
    },
  },
  /** Single-order fees (B2C). */
  order: {
    flat_fee_cents: makeIntegerAmount(1_00),
    employee_savings_flat_fee_cents: makeIntegerAmount(0),
    high_value_threshold_cents: makeIntegerAmount(500_00),
    high_value_fee_rate: makeDecimalAmount(0.002, SERVICE_FEE_RATE_SCALE),
    /** Computed fee below this value rounds to 0. */
    negligible_fee_threshold_cents: makeIntegerAmount(15),
    min_chargeable_fee_cents: makeIntegerAmount(15),
  },
  /**
   * Single PEA order fees (B2C personal account). 3-tier:
   *   amount <= low_threshold                  → below_low_threshold_rate
   *   low_threshold < amount <= high_threshold → flat_fee_cents
   *   amount > high_threshold                  → above_high_threshold_rate
   * Values currently mirror `investment_plan.service_fee`, but the two are independent on purpose
   * — single-order pricing and recurring-plan pricing can evolve separately.
   */
  pea_order: {
    flat_fee_cents: makeIntegerAmount(1_00),
    low_threshold_cents: makeIntegerAmount(200_00),
    high_threshold_cents: makeIntegerAmount(500_00),
    below_low_threshold_rate: makeDecimalAmount(0.005, SERVICE_FEE_RATE_SCALE),
    above_high_threshold_rate: makeDecimalAmount(0.002, SERVICE_FEE_RATE_SCALE),
  },
  crypto: {
    fee_free_max_amount_cents: makeIntegerAmount(250_00),
    high_value_threshold_cents: makeIntegerAmount(100_00),
    high_value_fee_rate: makeDecimalAmount(0.01, ANNUAL_RATE_SCALE),
  },
  /** WM company / customer custody + distributor configuration (recurring AUM fee, NOT per execution). */
  wm_company: {
    custody_rate: {
      gia: {
        min_rate: makeDecimalAmount(0.0001, WM_COMPANY_RATE_SCALE),
        max_rate: wmCustodyMaxRate.gia,
      },
      pea: {
        min_rate: makeDecimalAmount(0.0001, WM_COMPANY_RATE_SCALE),
        max_rate: wmCustodyMaxRate.pea,
      },
      per: {
        min_rate: makeDecimalAmount(0.0001, WM_COMPANY_RATE_SCALE),
        max_rate: wmCustodyMaxRate.per,
      },
    },
    distributor_rate: {
      gia: {
        min_rate: makeDecimalAmount(0, WM_COMPANY_RATE_SCALE),
        max_rate: makeDecimalAmount(0.00424, WM_COMPANY_RATE_SCALE),
      },
      pea: {
        min_rate: makeDecimalAmount(0, WM_COMPANY_RATE_SCALE),
        max_rate: makeDecimalAmount(0.00304, WM_COMPANY_RATE_SCALE),
      },
      per: {
        min_rate: makeDecimalAmount(0, WM_COMPANY_RATE_SCALE),
        max_rate: makeDecimalAmount(0.005, WM_COMPANY_RATE_SCALE),
      },
    },
    custody_plus_distributor_max_rate: wmCustodyMaxRate,
    total_fee_max_rate: wmTotalFeeMaxRate,
    /** Allowed range for the WM management fee rate stored on the plan. */
    wm_management_fee_rate: {
      min_rate: makeDecimalAmount(0, WM_COMPANY_RATE_SCALE),
      max_rate: makeDecimalAmount(0.012, WM_COMPANY_RATE_SCALE),
    },
  },
} as const;

const wmCustodyRate = FEES.wm_company.custody_rate;
const wmDistributorRate = FEES.wm_company.distributor_rate;
const wmCustodyPlusDistributorMaxRate = FEES.wm_company.custody_plus_distributor_max_rate;

const toWmFeePercentagePoints = (rate: number) => Number(rate) * 100;

/** Shares custody fee field bounds (percentage points). Sourced from `FEES.wm_company.custody_rate`. */

/** @see FEES.wm_company.custody_rate.gia.min_rate */
export const MIN_CTO_CUSTODY_FEE_PERCENTAGE_RATE = toWmFeePercentagePoints(
  wmCustodyRate.gia.min_rate,
);
/** @see FEES.wm_company.custody_rate.gia.max_rate */
export const MAX_CTO_CUSTODY_FEE_PERCENTAGE_RATE = toWmFeePercentagePoints(
  wmCustodyRate.gia.max_rate,
);
/** @see FEES.wm_company.distributor_rate.gia.max_rate */
export const MAX_CTO_DISTRIBUTOR_FEE_PERCENTAGE_RATE = toWmFeePercentagePoints(
  wmDistributorRate.gia.max_rate,
);

/** @see FEES.wm_company.custody_rate.pea.min_rate */
export const MIN_PEA_CUSTODY_FEE_PERCENTAGE_RATE = toWmFeePercentagePoints(
  wmCustodyRate.pea.min_rate,
);
/** @see FEES.wm_company.custody_rate.pea.max_rate */
export const MAX_PEA_CUSTODY_FEE_PERCENTAGE_RATE = toWmFeePercentagePoints(
  wmCustodyRate.pea.max_rate,
);
/** @see FEES.wm_company.distributor_rate.pea.max_rate */
export const MAX_PEA_DISTRIBUTOR_FEE_PERCENTAGE_RATE = toWmFeePercentagePoints(
  wmDistributorRate.pea.max_rate,
);

/** @see FEES.wm_company.custody_rate.per.min_rate */
export const MIN_PER_CUSTODY_FEE_PERCENTAGE_RATE = toWmFeePercentagePoints(
  wmCustodyRate.per.min_rate,
);
/** @see FEES.wm_company.custody_rate.per.max_rate */
export const MAX_PER_CUSTODY_FEE_PERCENTAGE_RATE = toWmFeePercentagePoints(
  wmCustodyRate.per.max_rate,
);

/**
 * Company fees-submit caps (percentage points): shares custody + distributor only.
 * Sourced from `FEES.wm_company.custody_plus_distributor_max_rate`.
 */
/** @see FEES.wm_company.custody_plus_distributor_max_rate.gia */
export const MAX_CTO_CUSTODY_PLUS_DISTRIBUTOR_FEE_PERCENTAGE_RATE = toWmFeePercentagePoints(
  wmCustodyPlusDistributorMaxRate.gia,
);
/** @see FEES.wm_company.custody_plus_distributor_max_rate.pea */
export const MAX_PEA_CUSTODY_PLUS_DISTRIBUTOR_FEE_PERCENTAGE_RATE = toWmFeePercentagePoints(
  wmCustodyPlusDistributorMaxRate.pea,
);
/** @see FEES.wm_company.custody_plus_distributor_max_rate.per */
export const MAX_PER_CUSTODY_PLUS_DISTRIBUTOR_FEE_PERCENTAGE_RATE = toWmFeePercentagePoints(
  wmCustodyPlusDistributorMaxRate.per,
);

/**
 * Total fee caps (percentage points) for customer assignment: shares + distributor + WM.
 * Sourced from `FEES.wm_company.total_fee_max_rate`.
 */
/** @see FEES.wm_company.total_fee_max_rate.gia */
export const MAX_CTO_TOTAL_CUSTODY_FEE_PERCENTAGE_RATE = toWmFeePercentagePoints(
  wmTotalFeeMaxRate.gia,
);
/** @see FEES.wm_company.total_fee_max_rate.pea */
export const MAX_PEA_TOTAL_CUSTODY_FEE_PERCENTAGE_RATE = toWmFeePercentagePoints(
  wmTotalFeeMaxRate.pea,
);
/** @see FEES.wm_company.total_fee_max_rate.per */
export const MAX_PER_TOTAL_CUSTODY_FEE_PERCENTAGE_RATE = toWmFeePercentagePoints(
  wmTotalFeeMaxRate.per,
);

/** @see FEES.wm_company.distributor_rate.per.max_rate */
export const MAX_PER_DISTRIBUTOR_FEE_PERCENTAGE_RATE = toWmFeePercentagePoints(
  wmDistributorRate.per.max_rate,
);
/**
 * @see MAX_PER_CUSTODY_PLUS_DISTRIBUTOR_FEE_PERCENTAGE_RATE, MIN_PER_CUSTODY_FEE_PERCENTAGE_RATE
 */
export const MAX_PER_DISTRIBUTOR_FEE_PERCENTAGE_RATE_UNDER_SUM_CAP =
  MAX_PER_CUSTODY_PLUS_DISTRIBUTOR_FEE_PERCENTAGE_RATE - MIN_PER_CUSTODY_FEE_PERCENTAGE_RATE;

/** Scale/precision used by daily management-fee math (decimal-amount construction). */
export const MANAGEMENT_FEE_SCALE = 2;
export const MANAGEMENT_FEE_DECIMAL_PLACES = 9;
